import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getSelf } from "@/api/auth";
import { listSessions, getAlphaStats, type Session } from "@/api/alpha";
import { transferSimAccount } from "@/api/sim";
import { listResearchReports } from "@/api/research";
import { useSubscription } from "@/hooks/useSubscription";
import { useRealtimeAccount } from "@/hooks/useRealtimeAccount";
import CountdownTimer from "@/components/feature/CountdownTimer";

export default function Profile() {
  const navigate = useNavigate();
  const sub = useSubscription();
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [reportCount, setReportCount] = useState(0);
  const [aiStats, setAiStats] = useState({ todayCards: 0, runningSessions: 0 });
  const [showFundEditor, setShowFundEditor] = useState(false);
  const [fundAmount, setFundAmount] = useState(0);
  const [saving, setSaving] = useState(false);
  const { account, refresh: loadAccount } = useRealtimeAccount();

  useEffect(() => {
    getSelf().then(setUser).catch(() => {});
    listSessions().then((d) => {
      const s = d.sessions?.[0] || null;
      setSession(s);
    }).catch(() => {});
    getAlphaStats().then((d) => setAiStats({
      todayCards: d.todayCards || 0,
      runningSessions: d.runningSessions || 0,
    })).catch(() => {});
    listResearchReports().then((d) => setReportCount(d.list?.length || 0)).catch(() => {});
  }, []);

  const transferFund = async () => {
    if (fundAmount === 0) return;
    setSaving(true);
    try {
      await transferSimAccount(fundAmount);
      loadAccount();
      setShowFundEditor(false);
    } catch (e) { alert(e instanceof Error ? e.message : "操作失败"); }
    finally { setSaving(false); }
  };

  const cashBalance = account?.cashBalance ?? session?.cashBalance ?? 0;
  const totalAsset = account?.totalValue ?? session?.totalAsset ?? 0;
  const marketValue = account?.totalMarketValue ?? 0;

  return (
    <div className="flex-1 px-4 py-4">
      {/* User card */}
      <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
            <i className="ri-user-3-line text-primary-500 text-xl" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-base font-bold text-foreground-950">{user?.displayName || user?.username || "用户"}</span>
              {sub.subscribed ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 font-medium border border-amber-200">天鼎AI</span>
              ) : (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-400">普通用户</span>
              )}
            </div>
            <span className="text-xs text-foreground-400">id: {user?.id || "****"}</span>
          </div>
        </div>
      </div>

      {/* 会员信息（付费用户） */}
      {sub.subscribed && (
        <div
          className="bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl p-5 mb-4 cursor-pointer"
          onClick={() => window.open("https://work.weixin.qq.com/ca/cawcdec9be027ee5f2?customer_channel=ADS_PR20260715134302488516", "_blank")}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
              <i className="ri-vip-crown-2-fill text-white text-lg" />
            </div>
            <div>
              <div className="text-white font-bold text-sm leading-tight">天鼎AI</div>
              <div className="text-white/60 text-[11px] leading-tight mt-0.5">{sub.priceLabel || "已订阅"}</div>
            </div>
          </div>
          <CountdownTimer expiresAt={sub.expiresAt} remainingDays={sub.remainingDays} />
        </div>
      )}

      {/* 推广卡片（免费用户） */}
      {!sub.subscribed && !sub.loading && (
        <div className="bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl p-5 mb-4">
          <h2 className="text-lg font-bold text-white mb-1">天鼎AI</h2>
          <p className="text-sm text-white/80 mb-3">解锁 AI 投资助手全部功能</p>
          <div className="space-y-1 mb-4">
            {["AI 股权研报生成器", "AI 交易员 · 深度对话", "AI 策略管理 · 自定义策略", "实时 AI 研判推送", "AI 自选股 · 智能提醒"].map((f) => (
              <div key={f} className="flex items-center gap-2 text-xs text-white/90"><i className="ri-check-line text-green-300" />{f}</div>
            ))}
          </div>
          <button onClick={() => window.open("https://work.weixin.qq.com/ca/cawcdec9be027ee5f2?customer_channel=ADS_PR20260715134302488516", "_blank")}
            className="w-full py-2.5 rounded-lg text-sm font-semibold bg-white text-primary-600 cursor-pointer hover:bg-white/90 transition-colors">
            了解详情
          </button>
        </div>
      )}

      {/* 资产概况 */}
      <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground-950">模拟资产</h3>
          {session && (
            <button onClick={() => { loadAccount(); setShowFundEditor(true); }}
              className="text-[10px] px-2.5 py-1 rounded-full bg-primary-500 text-white cursor-pointer whitespace-nowrap">
              资金管理
            </button>
          )}
        </div>
        {session ? (
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-foreground-500">总资产</span>
              <span className="font-semibold text-foreground-950">¥{Math.round(totalAsset).toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-foreground-500">持仓市值</span>
              <span className="font-semibold text-foreground-950">¥{Math.round(marketValue).toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-foreground-500">可用现金</span>
              <span className="font-semibold text-foreground-950">¥{Math.round(cashBalance).toLocaleString()}</span>
            </div>
          </div>
        ) : (
          <p className="text-xs text-foreground-400 mt-0.5">暂无运行中模拟盘</p>
        )}
      </div>

      {/* 功能菜单 */}
      <div className="bg-white rounded-xl border border-background-200/60 overflow-hidden mb-4">
        <div className="divide-y divide-background-100">
          <button onClick={() => navigate("/profile/about/contact")}
              className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-background-50 transition-colors">
              <i className="ri-customer-service-2-line text-foreground-400 text-lg" />
              <span className="text-sm text-foreground-700 flex-1 text-left">联系客服</span>
              <i className="ri-arrow-right-s-line text-foreground-300" />
            </button>
          <button onClick={() => navigate("/research/library")}
            className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-background-50 transition-colors">
            <i className="ri-file-list-3-line text-foreground-400 text-lg" />
            <span className="text-sm text-foreground-700 flex-1 text-left">我的研报</span>
            <span className="text-xs text-foreground-400 mr-1">{reportCount} 份</span>
            <i className="ri-arrow-right-s-line text-foreground-300" />
          </button>
          <button onClick={() => navigate("/reports")}
            className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-background-50 transition-colors">
            <i className="ri-bar-chart-grouped-line text-foreground-400 text-lg" />
            <span className="text-sm text-foreground-700 flex-1 text-left">收益报表</span>
            <i className="ri-arrow-right-s-line text-foreground-300" />
          </button>
          <button onClick={() => navigate("/profile/stats")}
            className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-background-50 transition-colors">
            <i className="ri-bar-chart-2-line text-foreground-400 text-lg" />
            <span className="text-sm text-foreground-700 flex-1 text-left">使用统计</span>
            <span className="text-xs text-foreground-400 mr-1">今日 {aiStats.todayCards}</span>
            <i className="ri-arrow-right-s-line text-foreground-300" />
          </button>
          <button onClick={() => navigate("/profile/account")}
            className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-background-50 transition-colors">
            <i className="ri-user-settings-line text-foreground-400 text-lg" />
            <span className="text-sm text-foreground-700 flex-1 text-left">账户设置</span>
            <i className="ri-arrow-right-s-line text-foreground-300" />
          </button>
          <button onClick={() => navigate("/profile/about")}
            className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-background-50 transition-colors">
            <i className="ri-information-line text-foreground-400 text-lg" />
            <span className="text-sm text-foreground-700 flex-1 text-left">关于</span>
            <i className="ri-arrow-right-s-line text-foreground-300" />
          </button>
        </div>
      </div>

      {/* 资金管理弹窗 */}
      {showFundEditor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" onClick={() => setShowFundEditor(false)}>
          <div className="bg-white w-full max-w-sm rounded-2xl p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-foreground-950">资金管理</h2>
              <button onClick={() => setShowFundEditor(false)} className="w-7 h-7 rounded-full bg-background-100 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line text-foreground-500 text-sm" />
              </button>
            </div>

            {/* 当前账户概况 */}
            <div className="bg-background-50 rounded-lg p-3 mb-4 space-y-1.5">
              <div className="flex justify-between text-[11px]">
                <span className="text-foreground-500">总资产</span>
                <span className="font-semibold text-foreground-950">¥{Math.round(totalAsset).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-[11px]">
                <span className="text-foreground-500">持仓市值</span>
                <span className="font-semibold text-foreground-950">¥{Math.round(marketValue).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-[11px]">
                <span className="text-foreground-500">可用现金</span>
                <span className="font-semibold text-foreground-950">¥{Math.round(cashBalance).toLocaleString()}</span>
              </div>
            </div>

            {/* 操作说明 */}
            <div className="bg-amber-50 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <i className="ri-information-line text-amber-500 text-sm mt-0.5" />
                <div>
                  <p className="text-[11px] font-medium text-amber-800">调整可用资金</p>
                  <p className="text-[10px] text-amber-600 mt-0.5">输入正数追加资金，输入负数提取资金。不改变现有持仓。</p>
                </div>
              </div>
            </div>

            <p className="text-xs text-foreground-600 mb-2 font-medium">要追加或提取的金额</p>
            <div className="relative mb-2">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400 font-semibold">¥</span>
              <input type="number" value={fundAmount}
                onChange={(e) => setFundAmount(parseInt(e.target.value) || 0)}
                className="w-full bg-background-50 rounded-xl py-3 pl-8 pr-3 text-lg font-bold text-foreground-950 outline-none border-none"
                placeholder="输入正数追加，输入负数提取" />
            </div>
            <div className="flex gap-2 mb-2">
              <button onClick={() => setFundAmount(50000)} className="flex-1 py-2 rounded-lg text-[11px] font-medium bg-background-100 text-foreground-600 cursor-pointer hover:bg-background-200">+5万</button>
              <button onClick={() => setFundAmount(100000)} className="flex-1 py-2 rounded-lg text-[11px] font-medium bg-background-100 text-foreground-600 cursor-pointer hover:bg-background-200">+10万</button>
              <button onClick={() => setFundAmount(500000)} className="flex-1 py-2 rounded-lg text-[11px] font-medium bg-background-100 text-foreground-600 cursor-pointer hover:bg-background-200">+50万</button>
              <button onClick={() => setFundAmount(-Math.round(cashBalance))} className="flex-1 py-2 rounded-lg text-[11px] font-medium bg-background-100 text-foreground-600 cursor-pointer hover:bg-background-200">提取全部</button>
            </div>
            <p className="text-[10px] text-foreground-400 mb-4 leading-relaxed">
              正数=追加资金，负数=提取资金。最多可提取 <span className="font-semibold">¥{Math.round(cashBalance).toLocaleString()}</span>。
              调整后可用现金实时更新，AI 基于新资金交易，<span className="font-semibold">不影响现有持仓</span>。
            </p>

            <div className="flex gap-3">
              <button onClick={() => setShowFundEditor(false)}
                className="flex-1 py-3 rounded-xl text-sm font-medium bg-background-100 text-foreground-600 cursor-pointer">
                取消
              </button>
              <button onClick={transferFund} disabled={saving || fundAmount === 0}
                className="flex-1 py-3 rounded-xl text-sm font-semibold bg-primary-500 text-white cursor-pointer disabled:opacity-50">
                {saving ? "处理中..." : "确认调整"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
